import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {Course, CreateCourseRequest, UpdateCourseRequest} from '../types';
import StorageService from "@/services/StorageService.ts";
import LearningStatsService from '../../../services/LearningStatsService';
import { LEARNING_STATS_QUERY_KEYS } from './learningStatsApi';

// Query keys
export const QUERY_KEYS = {
    courses: ['courses'] as const,
} as const;

// Create a new course
const createCourse = async (request: CreateCourseRequest): Promise<Course> => {
    try {
        // Generate a unique ID
        const id = Date.now().toString();

        const course: Course = {
            id,
            name: request.name.trim(),
            lectureCount: request.lectureCount,
            watchedLectures: request.watchedLectures ?? 0, // Default to 0 if not provided
            createdAt: new Date().toISOString(),
            lastAccessed: new Date().toISOString(),
        };

        console.log('Creating new course:', course);
        
        // Save to Firebase
        const existingCourses = await StorageService.getCourses();
        console.log('Fetched existing courses:', existingCourses);
        
        const updatedCourses = [...existingCourses, course];
        const success = await StorageService.setCourses(updatedCourses);
        
        if (!success) {
            throw new Error('Failed to save course to Firebase');
        }
        
        console.log('Course saved successfully');
        return course;
    } catch (error) {
        console.error('Error creating course:', error);
        throw error;
    }
};

// Update an existing course
const updateCourse = async (courseId: string, updates: UpdateCourseRequest): Promise<Course> => {
  const existingCourses = await StorageService.getCourses();
  const courseIndex = existingCourses.findIndex(course => course.id === courseId);
  
  if (courseIndex === -1) {
    throw new Error('Course not found');
  }

  const course = existingCourses[courseIndex];
  
  const updatedCourse: Course = {
    ...course,
    ...updates,
    name: updates.name?.trim() || course.name,
    lastAccessed: new Date().toISOString(),
  };

  const updatedCourses = [...existingCourses];
  updatedCourses[courseIndex] = updatedCourse;
  await StorageService.setCourses(updatedCourses);

  return updatedCourse;
};

// Increment watched lectures for a course
const incrementWatchedLectures = async (courseId: string): Promise<Course> => {
  const existingCourses = await StorageService.getCourses();
  const courseIndex = existingCourses.findIndex(course => course.id === courseId);
  
  if (courseIndex === -1) {
    throw new Error('Course not found');
  }

  const course = existingCourses[courseIndex];
  
  // Don't increment if already completed
  if (course.watchedLectures >= course.lectureCount) {
    return course;
  }

  const updatedCourse: Course = {
    ...course,
    watchedLectures: course.watchedLectures + 1,
    lastAccessed: new Date().toISOString(),
  };

  const updatedCourses = [...existingCourses];
  updatedCourses[courseIndex] = updatedCourse;
  await StorageService.setCourses(updatedCourses);

  // Record learning stats
  await LearningStatsService.recordLectureWatch(courseId);

  return updatedCourse;
};

// Get all courses
const getCourses = async (): Promise<Course[]> => {
    return await StorageService.getCourses();
};

// React Query hooks
export const useGetCourses = () => {
    return useQuery({
        queryKey: QUERY_KEYS.courses,
        queryFn: getCourses,
    });
};

export const useCreateCourse = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: createCourse,
        onSuccess: () => {
            // Invalidate courses query to refetch the updated list
            queryClient.invalidateQueries({queryKey: QUERY_KEYS.courses});
        },
    });
};

export const useIncrementWatchedLectures = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: incrementWatchedLectures,
    onSuccess: () => {
      // Invalidate courses query to refetch the updated list
      queryClient.invalidateQueries({queryKey: QUERY_KEYS.courses});
      // Invalidate learning stats queries
      queryClient.invalidateQueries({queryKey: LEARNING_STATS_QUERY_KEYS.stats});
      queryClient.invalidateQueries({queryKey: LEARNING_STATS_QUERY_KEYS.todayProgress});
    },
  });
}; 

export const useUpdateCourse = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ courseId, updates }: { courseId: string; updates: UpdateCourseRequest }) => 
      updateCourse(courseId, updates),
    onSuccess: () => {
      // Invalidate courses query to refetch the updated list
      queryClient.invalidateQueries({queryKey: QUERY_KEYS.courses});
      // Invalidate learning stats queries in case watched lectures changed
      queryClient.invalidateQueries({queryKey: LEARNING_STATS_QUERY_KEYS.stats});
      queryClient.invalidateQueries({queryKey: LEARNING_STATS_QUERY_KEYS.todayProgress});
    },
  });
}; 